/**
 * Unified Toast API
 *
 * Centralized toast notification system with adaptive duration,
 * undo support, and persistent toasts.
 *
 * Features:
 * - Adaptive duration based on message length
 * - Error toasts get 1.5x longer duration
 * - Built-in undo support with "Undo" button
 * - Promise-based toasts for async operations
 * - Persistent toasts that don't auto-dismiss
 *
 * @example
 * ```tsx
 * import { toast } from 'lib/toast-unified';
 *
 * // Basic usage
 * toast.success('Data saved successfully!');
 * toast.error('Processing error', { description: 'Try again' });
 *
 * // With undo action
 * toast.withUndo('Item removido', () => restoreItem());
 *
 * // Promise-based
 * toast.promise(saveData(), {
 *   loading: 'Saving...',
 *   success: 'Saved successfully!',
 *   error: 'Error saving',
 * });
 *
 * // Persistent toast
 * const id = toast.persistent('Processing in progress...');
 * // Later: toast.dismiss(id);
 * ```
 */

import { toast as sonnerToast, type ExternalToast } from 'sonner';

// =============================================================================
// Constants
// =============================================================================

/** Minimum toast duration in milliseconds */
const MIN_DURATION = 3000;

/** Maximum toast duration in milliseconds */
const MAX_DURATION = 10000;

/** Multiplier for error toast duration */
const ERROR_DURATION_MULTIPLIER = 1.5;

/** Default duration for undo toasts in milliseconds */
const UNDO_DURATION = 6000;

/** Reading speed: characters per millisecond (approx 200 words/min) */
const CHARS_PER_MS = 0.015;

/** Undo button label */
const UNDO_LABEL = 'Desfazer';

// =============================================================================
// Types
// =============================================================================

export interface ToastOptions extends Omit<ExternalToast, 'duration'> {
  /** Toast duration in milliseconds */
  duration?: number;
  /** Additional description text */
  description?: string;
}

export interface PromiseMessages<T> {
  /** Message shown while promise is pending */
  loading: string;
  /** Message shown on success (can be a function receiving the result) */
  success: string | ((data: T) => string);
  /** Message shown on error (can be a function receiving the error) */
  error: string | ((error: Error) => string);
}

export type ToastId = string | number;

// =============================================================================
// Duration Calculation
// =============================================================================

/**
 * Calculates adaptive duration based on message length and type.
 *
 * The duration is calculated by:
 * 1. Estimating reading time based on character count
 * 2. Adding time for description if present
 * 3. Clamping between MIN_DURATION and MAX_DURATION
 * 4. Applying multiplier for error messages
 *
 * @param message - The main toast message
 * @param type - The toast type ('success' | 'error' | 'warning' | 'info')
 * @param description - Optional description text
 * @returns Duration in milliseconds
 */
export function getDuration(
  message: string,
  type: 'success' | 'error' | 'warning' | 'info' = 'info',
  description?: string
): number {
  // Calculate total text length including description
  const totalLength = message.length + (description?.length || 0);

  // Calculate base duration from reading time
  // Adding a base minimum to ensure short messages still display long enough
  const readingTime = totalLength / CHARS_PER_MS;
  let duration = Math.max(MIN_DURATION, readingTime);

  // Apply error multiplier
  if (type === 'error') {
    duration *= ERROR_DURATION_MULTIPLIER;
  }

  // Clamp to maximum duration
  return Math.min(duration, MAX_DURATION);
}

// =============================================================================
// Toast API
// =============================================================================

/**
 * Unified toast notification API.
 *
 * Provides consistent toast notifications with adaptive duration,
 * undo support, and promise handling.
 */
export const toast = {
  /**
   * Show a success toast with adaptive duration.
   *
   * @param message - Success message to display
   * @param options - Optional toast configuration
   * @returns Toast ID for dismissal
   *
   * @example
   * toast.success('Data saved successfully!');
   * toast.success('Company created', { description: 'ID: 12345' });
   */
  success: (message: string, options?: ToastOptions): ToastId => {
    const duration = options?.duration ?? getDuration(message, 'success', options?.description);
    return sonnerToast.success(message, {
      ...options,
      duration,
    });
  },

  /**
   * Show an error toast with extended adaptive duration.
   * Error toasts automatically get 1.5x longer duration.
   *
   * @param message - Error message to display
   * @param options - Optional toast configuration
   * @returns Toast ID for dismissal
   *
   * @example
   * toast.error('Error processing request');
   * toast.error('Connection failed', { description: 'Check your internet' });
   */
  error: (message: string, options?: ToastOptions): ToastId => {
    const duration = options?.duration ?? getDuration(message, 'error', options?.description);
    return sonnerToast.error(message, {
      ...options,
      duration,
    });
  },

  /**
   * Show a warning toast with adaptive duration.
   *
   * @param message - Warning message to display
   * @param options - Optional toast configuration
   * @returns Toast ID for dismissal
   *
   * @example
   * toast.warning('Warning: unsaved data pending');
   */
  warning: (message: string, options?: ToastOptions): ToastId => {
    const duration = options?.duration ?? getDuration(message, 'warning', options?.description);
    return sonnerToast.warning(message, {
      ...options,
      duration,
    });
  },

  /**
   * Show an info toast with adaptive duration.
   *
   * @param message - Info message to display
   * @param options - Optional toast configuration
   * @returns Toast ID for dismissal
   *
   * @example
   * toast.info('Processing started');
   */
  info: (message: string, options?: ToastOptions): ToastId => {
    const duration = options?.duration ?? getDuration(message, 'info', options?.description);
    return sonnerToast.info(message, {
      ...options,
      duration,
    });
  },

  /**
   * Show a loading toast that persists until dismissed.
   * Returns a dismiss function for convenience.
   *
   * @param message - Loading message to display
   * @returns Function to dismiss the loading toast
   *
   * @example
   * const dismiss = toast.loading('Loading data...');
   * await fetchData();
   * dismiss();
   * toast.success('Data loaded!');
   */
  loading: (message: string): (() => void) => {
    const toastId = sonnerToast.loading(message);
    return () => sonnerToast.dismiss(toastId);
  },

  /**
   * Show a toast with an undo action button.
   * Useful for destructive actions that can be reverted.
   *
   * @param message - Message to display
   * @param onUndo - Callback when undo button is clicked
   * @param options - Optional toast configuration
   * @returns Toast ID for dismissal
   *
   * @example
   * toast.withUndo('Item removido', () => {
   *   restoreItem(itemId);
   * });
   */
  withUndo: (message: string, onUndo: () => void, options?: ToastOptions): ToastId => {
    return sonnerToast(message, {
      ...options,
      duration: options?.duration ?? UNDO_DURATION,
      action: {
        label: UNDO_LABEL,
        onClick: onUndo,
      },
    });
  },

  /**
   * Show a persistent toast that never auto-dismisses.
   * Must be manually dismissed using toast.dismiss(id).
   *
   * @param message - Message to display
   * @param options - Optional toast configuration
   * @returns Toast ID for dismissal
   *
   * @example
   * const id = toast.persistent('Upload in progress...');
   * // When done:
   * toast.dismiss(id);
   */
  persistent: (message: string, options?: ToastOptions): ToastId => {
    return sonnerToast(message, {
      ...options,
      duration: Infinity,
    });
  },

  /**
   * Show a promise-based toast that updates based on promise state.
   * Automatically handles loading, success, and error states.
   *
   * @param promise - Promise to track
   * @param messages - Messages for each state
   * @returns The original promise (pass-through)
   *
   * @example
   * const result = await toast.promise(
   *   saveUserData(data),
   *   {
   *     loading: 'Saving data...',
   *     success: 'Data saved successfully!',
   *     error: 'Error saving data',
   *   }
   * );
   */
  promise: <T>(promise: Promise<T>, messages: PromiseMessages<T>): Promise<T> => {
    sonnerToast.promise(promise, {
      loading: messages.loading,
      success: data => {
        const successMessage =
          typeof messages.success === 'function' ? messages.success(data) : messages.success;
        return successMessage;
      },
      error: err => {
        const errorMessage =
          typeof messages.error === 'function' ? messages.error(err) : messages.error;
        return errorMessage;
      },
    });
    return promise;
  },

  /**
   * Dismiss a specific toast by ID.
   *
   * @param id - Toast ID to dismiss
   *
   * @example
   * const id = toast.info('Processing...');
   * // Later:
   * toast.dismiss(id);
   */
  dismiss: (id?: ToastId): void => {
    sonnerToast.dismiss(id);
  },

  /**
   * Dismiss all active toasts.
   *
   * @example
   * toast.dismissAll();
   */
  dismissAll: (): void => {
    sonnerToast.dismiss();
  },
};

export default toast;
